<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Student Residences"
      subtitle="Manage boarding and residence records"
      icon="fas fa-house-user"
      gradient="green"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableData.filter.search"
            placeholder="search student, boarding house, owner"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Stay Type"
          label-for="filterStayType"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableData.selected.stay_type"
            input-id="filterStayType"
            :options="stayTypeOptions"
            clearable
            append-to-body
            :calculate-position="calculateDropPosition"
            :disabled="tableData.busy"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Verified"
          label-for="filterVerified"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableData.selected.is_verified"
            input-id="filterVerified"
            label="name"
            :options="verifiedOptions"
            clearable
            append-to-body
            :calculate-position="calculateDropPosition"
            :disabled="tableData.busy"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Action"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BButton
            size="sm"
            type="button"
            variant="primary"

            @click.prevent="createItem"
          >
            Add Residence
          </BButton>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableData"
      :provider="tableProvider"
      :fields="tableData.fields"
      :filter="tableData.filter"
      :filter-on="tableData.filterOn"
      :sort-by="tableData.sortBy"
      :sort-desc="tableData.sortDesc"
      :sort-direction="tableData.sortDirection"
      :total-rows="tableData.totalRows"
      :current-page="tableData.currentPage"
      :per-page="tableData.perPage"
      :stacked="tableData.stacked"
      :busy="tableData.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableData.busy"
            @click.prevent="editItem(item)"
          >
            Edit
          </BButton>
          <BButton
            size="sm"
            variant="danger"
            :disabled="tableData.busy"
            @click.prevent="deleteItemConfirm(item)"
          >
            Delete
          </BButton>
        </div>
      </template>

      <template #cell(student)="{ item }">
        {{ item.student?.id_number || 'N/A' }} - {{ item.student?.first_name }} {{ item.student?.last_name }}
      </template>

      <template #cell(is_verified)="{ item }">
        <BBadge :variant="item.is_verified ? 'success' : 'secondary'">
          {{ item.is_verified ? 'Verified' : 'Pending' }}
        </BBadge>
      </template>
    </CustomTable>

    <BModal
      ref="modalForm"
      size="xl"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="state.editing ? 'Edit Residence' : 'Add Residence'"
    >
      <ValidationObserver ref="formData">
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="student_id"
                name="student"
                rules="required"
              >
                <BFormGroup
                  label="Student"
                  label-for="fStudent"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.student"
                    label="id_number"
                    append-to-body
                    :options="studentOptions"
                    :loading="fetchingStudents"
                    :disabled="state.busy || fetchingStudents"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  >
                    <template #option="{ id_number, first_name, last_name }">
                      <div>{{ id_number }} - {{ first_name }} {{ last_name }}</div>
                    </template>
                    <template #selected-option="{ id_number, first_name, last_name }">
                      <div>{{ id_number }} - {{ first_name }} {{ last_name }}</div>
                    </template>
                  </VSelect>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="boarding_house_name"
                name="boarding house name"
                rules="required|max:100"
              >
                <BFormGroup
                  label="Boarding House Name"
                  class="mb-2"
                >
                  <BFormInput
                    v-model="formData.boarding_house_name"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="full_address"
                name="full address"
                rules="required"
              >
                <BFormGroup
                  label="Full Address"
                  class="mb-2"
                >
                  <BFormTextarea
                    v-model="formData.full_address"
                    rows="2"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="owner_name"
                name="owner name"
                rules="required|max:100"
              >
                <BFormGroup
                  label="Owner Name"
                  class="mb-2"
                >
                  <BFormInput
                    v-model="formData.owner_name"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="owner_contact_number"
                name="owner contact number"
                rules="required|max:20"
              >
                <BFormGroup
                  label="Owner Contact Number"
                  class="mb-2"
                >
                  <BFormInput
                    v-model="formData.owner_contact_number"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="stay_type"
                name="stay type"
                rules="required"
              >
                <BFormGroup
                  label="Stay Type"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.stay_type"
                    :options="stayTypeOptions"
                    :disabled="state.busy"
                    append-to-body
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="room_or_unit"
                name="room or unit"
                rules="required|max:50"
              >
                <BFormGroup
                  label="Room or Unit"
                  class="mb-2"
                >
                  <BFormInput
                    v-model="formData.room_or_unit"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="date_started"
                name="date started"
                rules="required"
              >
                <BFormGroup
                  label="Date Started"
                  class="mb-2"
                >
                  <BFormInput
                    v-model="formData.date_started"
                    type="date"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="contact_person_name"
                name="contact person name"
                rules="required|max:100"
              >
                <BFormGroup
                  label="Contact Person Name"
                  class="mb-2"
                >
                  <BFormInput
                    v-model="formData.contact_person_name"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="contact_person_number"
                name="contact person number"
                rules="required|max:20"
              >
                <BFormGroup
                  label="Contact Person Number"
                  class="mb-2"
                >
                  <BFormInput
                    v-model="formData.contact_person_number"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="emergency_contact_name"
                name="emergency contact name"
                rules="required|max:100"
              >
                <BFormGroup
                  label="Emergency Contact Name"
                  class="mb-2"
                >
                  <BFormInput
                    v-model="formData.emergency_contact_name"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="emergency_contact_number"
                name="emergency contact number"
                rules="required|max:20"
              >
                <BFormGroup
                  label="Emergency Contact Number"
                  class="mb-2"
                >
                  <BFormInput
                    v-model="formData.emergency_contact_number"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="4"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="landmark"
                name="landmark"
                rules="max:100"
              >
                <BFormGroup
                  label="Landmark"
                  class="mb-2"
                >
                  <BFormInput
                    v-model="formData.landmark"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <BFormGroup class="mb-2">
                <BFormCheckbox
                  id="is_verified"
                  v-model="formData.is_verified"
                  switch
                  value="1"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Verified?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateForm"
        >
          {{ state.editing ? 'Update' : 'Create' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminStudentResidence, SSharedList } from '@/services'

export default {
  name: 'VAdminStudentResidences',

  components: { AdminPageHeader },

  mixins: [MxMisc, MxFormatter],

  middleware: ['auth', 'admin'],

  metaInfo: () => ({ title: 'Student Residences' }),

  data () {
    return {
      state: { busy: false, editing: false },
      selected: {
        student: null,
        stay_type: null
      },
      options: {
        students: []
      },
      studentOptions: [],
      fetchingStudents: false,
      stayTypeOptions: ['boarding', 'dormitory', 'apartment', 'house', 'condo', 'other'],
      verifiedOptions: [
        { id: 1, name: 'Verified' },
        { id: 0, name: 'Pending' }
      ],
      formData: {
        id: '',
        student_id: '',
        boarding_house_name: '',
        full_address: '',
        landmark: '',
        owner_name: '',
        owner_contact_number: '',
        contact_person_name: '',
        contact_person_number: '',
        stay_type: '',
        room_or_unit: '',
        date_started: '',
        emergency_contact_name: '',
        emergency_contact_number: '',
        is_verified: 0
      },
      tableData: {
        busy: false,
        selected: {
          stay_type: null,
          is_verified: null
        },
        filter: {
          search: '',
          stay_type: '',
          is_verified: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'updated_at',
        sortDesc: true,
        sortDirection: 'desc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'student', label: 'Student', class: 'min-w-225px' },
          { mobile: 2, key: 'boarding_house_name', class: 'min-w-175px' },
          { mobile: 3, key: 'stay_type', class: 'min-w-100px' },
          { mobile: 4, key: 'room_or_unit', class: 'min-w-100px' },
          { mobile: 5, key: 'is_verified', class: 'text-center' },
          { mobile: 6, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  watch: {
    'selected.student' (student) {
      this.formData.student_id = student?.id || ''
    },
    'selected.stay_type' (stayType) {
      this.formData.stay_type = stayType || ''
    },
    'tableData.selected.stay_type' (stayType) {
      this.tableData.filter.stay_type = stayType || ''
    },
    'tableData.selected.is_verified' (isVerified) {
      this.tableData.filter.is_verified = isVerified !== null && isVerified !== undefined ? isVerified.id : ''
    }
  },

  mounted () {
    this.fetchStudents()
  },

  methods: {
    async fetchStudents () {
      this.fetchingStudents = true
      return SSharedList.getActiveStudents({ per_page: 1000, sort: 'last_name', sort_desc: false }).then(({ data: response }) => {
        const payload = response?.data || response
        this.studentOptions = payload || []
      }).catch(() => {
        this.studentOptions = []
      }).finally(() => {
        this.fetchingStudents = false
      })
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableData.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        stay_type: filter.stay_type,
        is_verified: filter.is_verified
      }

      return SAdminStudentResidence.getList(payload)
        .then(({ data: response }) => {
          const data = response?.data || response
          this.tableData.totalRows = data?.total_rows || 0
          return data?.items || []
        })
        .catch(() => {
          this.tableData.totalRows = 0
          return []
        })
        .finally(() => {
          this.tableData.busy = false
        })
    },

    createItem () {
      this.state.editing = false
      this.formData = {
        id: '',
        student_id: '',
        boarding_house_name: '',
        full_address: '',
        landmark: '',
        owner_name: '',
        owner_contact_number: '',
        contact_person_name: '',
        contact_person_number: '',
        stay_type: '',
        room_or_unit: '',
        date_started: '',
        emergency_contact_name: '',
        emergency_contact_number: '',
        is_verified: 0
      }
      this.selected.student = null
      this.selected.stay_type = null
      this.$refs.modalForm.show()
    },

    editItem (item) {
      this.state.editing = true
      this.formData = {
        id: item.id,
        student_id: item.student_id,
        boarding_house_name: item.boarding_house_name,
        full_address: item.full_address,
        landmark: item.landmark || '',
        owner_name: item.owner_name,
        owner_contact_number: item.owner_contact_number,
        contact_person_name: item.contact_person_name,
        contact_person_number: item.contact_person_number,
        stay_type: item.stay_type,
        room_or_unit: item.room_or_unit,
        date_started: item.date_started,
        emergency_contact_name: item.emergency_contact_name,
        emergency_contact_number: item.emergency_contact_number,
        is_verified: item.is_verified ? 1 : 0
      }
      this.selected.student = this.studentOptions.find(student => Number(student.id) === Number(item.student_id)) || null
      this.selected.stay_type = item.stay_type
      this.$refs.modalForm.show()
    },

    async validateForm () {
      await this.$refs.formData.validate().then(async validForm => {
        if (!validForm) return this.swalInvalid()

        this.swalConfirm({
          html: this.state.editing ? '<h6>Update this residence?</h6>' : '<h6>Create this residence?</h6>',
          confirmButtonText: this.state.editing ? 'Update' : 'Create',
          preConfirm: async () => {
            this.state.busy = true

            const action = this.state.editing
              ? SAdminStudentResidence.update(this.formData.id, this.formData)
              : SAdminStudentResidence.store(this.formData)

            return action.then(() => {
              this.$refs.modalForm.hide()
              this.$refs.tableData.refetch()
              this.swalSuccess(`Residence ${this.state.editing ? 'updated' : 'created'} successfully`)
            }).catch(({ message }) => {
              if (message?.errors) {
                this.$refs.formData.setErrors(message.errors)
              }
            }).finally(() => {
              this.state.busy = false
            })
          }
        })
      })
    },

    async deleteItemConfirm (item) {
      const result = await this.$swal({
        title: 'Delete residence',
        text: `Are you sure you want to delete residence record for ${item.student?.first_name || 'this student'}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        this.state.busy = true
        SAdminStudentResidence.destroy(item.id).then(() => {
          this.swalSuccess('Residence deleted successfully')
          this.$refs.tableData.refetch()
        }).catch(error => {
          this.swalError(error.response?.data?.message || 'Failed to delete residence')
        }).finally(() => {
          this.state.busy = false
        })
      }
    }
  }
}
</script>
